import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { signOut } from '../lib/supabase'
import LoginModal from './LoginModal'

export default function Navbar({ transparent = false }) {
  const { user, profile, role } = useAuth()
  const [menuOpen, setMenuOpen]       = useState(false)
  const [loginOpen, setLoginOpen]     = useState(false)
  const navigate = useNavigate()

  // Allow any component to trigger the login modal via custom event
  useEffect(() => {
    const handler = () => setLoginOpen(true)
    window.addEventListener('open-login', handler)
    return () => window.removeEventListener('open-login', handler)
  }, [])

  async function handleSignOut() {
    await signOut()
    setMenuOpen(false)
    navigate('/')
  }

const isTransparent = false
const navBg    = 'rgba(255,255,255,.97)'
const navBorder = '1px solid var(--gray-200)'
const navShadow = '0 1px 8px rgba(0,0,0,.06)'
const linkColor = 'var(--gray-700)'

  return (
    <>
      <nav className="navbar" style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 24px',
        background: navBg,
        backdropFilter: isTransparent ? 'none' : 'blur(12px)',
        borderBottom: navBorder,
        boxShadow: navShadow,
        transition: 'background .3s, box-shadow .3s',
      }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <img
            src="/logo.png"
            alt="Wind Sports Directory"
            style={{
              height: 44,
              width: 'auto',
              filter: 'none',
              transition: 'filter .2s',
            }}
          />
        </Link>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {user ? (
            <span style={{ fontSize: 13, color: linkColor, fontWeight: 500 }}>
              {profile?.username || user.email?.split('@')[0]}
              {role >= 100 && (
                <span style={{
                  marginLeft: 6, fontSize: 10, fontFamily: 'var(--mono)',
                  background: role >= 300 ? 'var(--gold)' : role >= 200 ? 'var(--blue)' : 'var(--green)',
                  color: '#fff', padding: '2px 6px', borderRadius: 4,
                }}>
                  {role >= 300 ? 'OWNER' : role >= 200 ? 'ADMIN' : 'MOD'}
                </span>
              )}
            </span>
          ) : (
            <button
              onClick={() => setLoginOpen(true)}
              style={{
                background: isTransparent ? '#fff' : 'var(--navy)',
                color: isTransparent ? 'var(--gray-900)' : '#fff',
                border: 'none', padding: '8px 18px', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Login
            </button>
          )}

          {/* Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              width: 40, height: 40, borderRadius: 8, cursor: 'pointer',
              background: isTransparent ? 'rgba(255,255,255,.15)' : 'var(--gray-100)',
              border: isTransparent ? '1.5px solid rgba(255,255,255,.3)' : '1.5px solid var(--gray-200)',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 4,
            }}
          >
            {[0,1,2].map(i => (
              <span key={i} style={{
                display: 'block', width: 16, height: 2,
                background: isTransparent ? '#fff' : 'var(--gray-600)',
                borderRadius: 2,
              }} />
            ))}
          </button>
        </div>
      </nav>

      {/* Drawer overlay */}
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,.2)' }}
        />
      )}

      {/* Drawer */}
      {menuOpen && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 280,
          background: '#fff', zIndex: 300, boxShadow: '-4px 0 24px rgba(0,0,0,.15)',
          display: 'flex', flexDirection: 'column', padding: '20px 0',
          animation: 'slideUp .2s ease',
        }}>
          {/* Header */}
          <div style={{ padding: '0 20px 16px', borderBottom: '1px solid var(--gray-100)', marginBottom: 8 }}>
            <img src="/logo.png" alt="Wind Sports Directory" style={{ height: 36, width: 'auto', marginBottom: 6 }} />
            {user && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{user.email}</div>}
          </div>

          {/* Nav links */}
          {[
            { to: '/',           label: '🏠  Home' },
            { to: '/directory',  label: '📋  Directory' },
            { to: '/submit',     label: '➕  Submit a Listing' },
            { to: '/globe',       label: '🌍  Globe View' },
            { to: '/vote',       label: '🏆  Vote This Month' },
            { to: '/blog',       label: '📝  Community Blog' },
            { to: '/news',       label: '📰  News & Updates' },
            ...(user ? [{ to: '/my-listings', label: '🗂  My Listings' }] : []),
          ].map(({ to, label }) => (
            <Link
              key={to} to={to}
              onClick={() => setMenuOpen(false)}
              style={{
                display: 'block', padding: '12px 24px', fontSize: 14,
                fontWeight: 500, color: 'var(--gray-700)',
                borderBottom: '1px solid var(--gray-50)', textDecoration: 'none',
              }}
            >
              {label}
            </Link>
          ))}

          <Link to="/support" onClick={() => setMenuOpen(false)}
            style={{ display:'block', padding:'12px 24px', fontSize:14, fontWeight:500, color:'var(--gray-700)', borderBottom:'1px solid var(--gray-50)', textDecoration:'none' }}
          >
            🛟  Support
          </Link>

          {/* Admin links */}
         {role >= 100 && (
  <Link to="/admin" onClick={() => setMenuOpen(false)}
    style={{ display: 'block', padding: '12px 24px', fontSize: 14, fontWeight: 600, color: 'var(--blue)', borderBottom: '1px solid var(--gray-50)', textDecoration: 'none' }}>
    ⚙️  Admin Dashboard
  </Link>
)}

          {/* Footer */}
          <div style={{ marginTop: 'auto', padding: '16px 24px', borderTop: '1px solid var(--gray-100)' }}>
            {user ? (
              <button onClick={handleSignOut} className="btn btn-ghost" style={{ width: '100%' }}>
                Sign Out
              </button>
            ) : (
              <button onClick={() => { setLoginOpen(true); setMenuOpen(false) }} className="btn btn-primary" style={{ width: '100%' }}>
                Login / Register
              </button>
            )}
          </div>
        </div>
      )}

      {loginOpen   && <LoginModal onClose={() => setLoginOpen(false)} />}
    </>
  )
}
